/**
 * Admin Panel Component
 *
 * Modal overlay showing:
 * 1. Cache status table for last 30 trading days
 * 2. Bulk actions (cache N days, refresh today)
 * 3. Live log tail via SSE
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import CONFIG from '../config';
import { authFetch, getStreamUrl } from '../auth/authFetch';

function AdminPanel({ visible, onClose }) {
  const [cacheStatus, setCacheStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logLevel, setLogLevel] = useState('WARNING');
  const [logConnected, setLogConnected] = useState(false);
  const [cachingDates, setCachingDates] = useState({});
  const [bulkDays, setBulkDays] = useState(7);
  const [activeTab, setActiveTab] = useState('cache'); // 'cache' | 'logs' | 'system'
  const [systemStatus, setSystemStatus] = useState(null);
  const [systemError, setSystemError] = useState(false);
  const logContainerRef = useRef(null);
  const logSourceRef = useRef(null);
  const autoScrollRef = useRef(true);

  // Fetch cache status
  const fetchCacheStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${CONFIG.apiUrl}/api/admin/cache-status`);
      if (res.ok) {
        const data = await res.json();
        setCacheStatus(data);
      }
    } catch (err) {
      console.error('[Admin] Failed to fetch cache status:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount and periodically
  useEffect(() => {
    if (!visible) return;
    fetchCacheStatus();
    const interval = setInterval(fetchCacheStatus, 15000);
    return () => clearInterval(interval);
  }, [visible, fetchCacheStatus]);

  // Fetch system status (endpoint 404s until the backend restart deploys it;
  // keep the last good payload and show a banner instead of crashing)
  const fetchSystemStatus = useCallback(async () => {
    try {
      const res = await authFetch(`${CONFIG.apiUrl}/api/admin/system-status`);
      if (res.ok) {
        setSystemStatus(await res.json());
        setSystemError(false);
      } else {
        setSystemError(true);
      }
    } catch (err) {
      console.error('[Admin] Failed to fetch system status:', err);
      setSystemError(true);
    }
  }, []);

  useEffect(() => {
    if (!visible || activeTab !== 'system') return;
    fetchSystemStatus();
    const interval = setInterval(fetchSystemStatus, 7000);
    return () => clearInterval(interval);
  }, [visible, activeTab, fetchSystemStatus]);

  // SSE log tail
  useEffect(() => {
    if (!visible || activeTab !== 'logs') {
      if (logSourceRef.current) {
        logSourceRef.current.close();
        logSourceRef.current = null;
        setLogConnected(false);
      }
      return;
    }

    let cancelled = false;
    let es = null;
    (async () => {
      let url;
      try {
        url = await getStreamUrl(`${CONFIG.apiUrl}/api/admin/logs?level=${logLevel}`);
      } catch {
        setLogConnected(false);
        return;
      }
      if (cancelled) return;
      es = new EventSource(url);
      logSourceRef.current = es;

      es.addEventListener('log', (e) => {
        try {
          const data = JSON.parse(e.data);
          setLogs(prev => {
            const next = [...prev, data];
            return next.length > 500 ? next.slice(-500) : next;
          });
        } catch {}
      });

      es.onopen = () => setLogConnected(true);
      es.onerror = () => setLogConnected(false);
    })();

    return () => {
      cancelled = true;
      if (es) es.close();
      logSourceRef.current = null;
      setLogConnected(false);
    };
  }, [visible, activeTab, logLevel]);

  // Auto-scroll logs
  useEffect(() => {
    if (autoScrollRef.current && logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  // Handle log container scroll
  const handleLogScroll = useCallback(() => {
    if (!logContainerRef.current) return;
    const el = logContainerRef.current;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    autoScrollRef.current = atBottom;
  }, []);

  // Cache a specific date
  const handleCacheDate = useCallback(async (dateStr, phases = ['es', 'greeks', 'oi', 'orderflow']) => {
    setCachingDates(prev => ({ ...prev, [dateStr]: true }));
    try {
      const res = await authFetch(`${CONFIG.apiUrl}/api/admin/cache-date`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: dateStr, phases }),
      });
      if (res.ok) {
        // Poll for completion
        setTimeout(() => fetchCacheStatus(), 3000);
        setTimeout(() => fetchCacheStatus(), 10000);
        setTimeout(() => fetchCacheStatus(), 30000);
      }
    } catch (err) {
      console.error('[Admin] Cache date failed:', err);
    } finally {
      setTimeout(() => {
        setCachingDates(prev => {
          const next = { ...prev };
          delete next[dateStr];
          return next;
        });
      }, 5000);
    }
  }, [fetchCacheStatus]);

  // Invalidate cache for a date/phase
  const handleInvalidate = useCallback(async (dateStr, phase) => {
    try {
      await authFetch(`${CONFIG.apiUrl}/api/admin/invalidate-cache`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: dateStr, phase }),
      });
      fetchCacheStatus();
    } catch (err) {
      console.error('[Admin] Invalidate failed:', err);
    }
  }, [fetchCacheStatus]);

  // Bulk cache last N days
  const handleBulkCache = useCallback(async () => {
    if (!cacheStatus?.dates) return;
    const datesToCache = cacheStatus.dates
      .slice(0, bulkDays)
      .filter(d => d.es.candles < 50 || d.greeks.candles < 50);
    for (const d of datesToCache) {
      await handleCacheDate(d.date);
    }
  }, [cacheStatus, bulkDays, handleCacheDate]);

  // Refresh today
  const handleRefreshToday = useCallback(async () => {
    try {
      await authFetch(`${CONFIG.apiUrl}/api/greeks/refresh`, { method: 'POST' });
    } catch (err) {
      console.error('[Admin] Refresh failed:', err);
    }
  }, []);

  if (!visible) return null;

  const statusColor = (count, validated) => {
    if (count >= 50 && validated) return '#4aff88';
    if (count > 0) return '#ffb84a';
    return '#ff4a4a';
  };

  const levelColor = (level) => {
    if (level === 'ERROR' || level === 'CRITICAL') return '#ff4a4a';
    if (level === 'WARNING') return '#ffb84a';
    return '#c0c8d8';
  };

  const fmtUptime = (sec) => {
    if (sec == null) return '-';
    const s = Math.floor(sec);
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (d > 0) return `${d}d ${h}h`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m ${s % 60}s`;
  };

  const ageColor = (sec) => {
    if (sec == null) return '#ff4a4a';
    if (sec < 15) return '#4aff88';
    if (sec < 30) return '#ffb84a';
    return '#ff4a4a';
  };

  const dot = (color) => (
    <span style={{
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      background: color,
      display: 'inline-block',
      marginRight: '6px',
      flexShrink: 0,
    }} />
  );

  // Per-stream health color; worst color across rows drives the summary card
  const quoteStreamColor = (qs) => {
    if (!qs) return '#ffb84a';
    if (qs.running && qs.last_tick_age != null && qs.last_tick_age < 120) return '#4aff88';
    return '#ffb84a';
  };
  const tokenColor = (tok) => {
    if (!tok || tok.refresh_token_days_left == null) return '#ffb84a';
    if (tok.refresh_token_days_left <= 0) return '#ff4a4a';
    if (tok.refresh_token_days_left < 2) return '#ffb84a';
    return '#4aff88';
  };
  const esTapeColor = (tape) => {
    if (!tape || tape.process_alive === false) return '#ff4a4a';
    if (tape.last_trade_age_s == null) return '#ffb84a';
    return '#4aff88';
  };
  const streamerColor = (st) => {
    if (st.heartbeat_age_s != null && st.heartbeat_age_s > 30) return '#ff4a4a';
    // Healing (startup heal or mid-session gap replay): blue, distinct
    // from down. Gated on a FRESH heartbeat so a streamer that died
    // mid-heal (blob lingers until its 30s TTL) stays red via the check
    // above; placed BEFORE the tick-age red because a long heal
    // legitimately has last_tick_age_s > 120.
    if (st.status === 'healing' && st.heartbeat_age_s != null && st.heartbeat_age_s <= 30) return '#4aa8ff';
    if (st.running && st.last_tick_age_s != null && st.last_tick_age_s > 120) return '#ff4a4a';
    if (st.last_tick_age_s == null) return '#ffb84a';
    return '#4aff88';
  };
  const worstColor = (colors) => {
    if (colors.includes('#ff4a4a')) return '#ff4a4a';
    if (colors.includes('#ffb84a')) return '#ffb84a';
    return '#4aff88';
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      zIndex: 10000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}>
      <div style={{
        background: '#1a1f2e',
        border: '1px solid #2a3344',
        borderRadius: '12px',
        width: '90vw',
        maxWidth: '1100px',
        height: '80vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #2a3344',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <h2 style={{ margin: 0, fontSize: '16px', color: '#e0e0e0' }}>Admin Panel</h2>
            <div style={{ display: 'flex', gap: '4px' }}>
              {['cache', 'logs', 'system'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '4px 12px',
                    borderRadius: '4px',
                    border: '1px solid #2a3344',
                    background: activeTab === tab ? '#4a9eff' : 'transparent',
                    color: activeTab === tab ? '#fff' : '#8a9ab0',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    textTransform: 'capitalize',
                  }}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#8a9ab0',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '4px 8px',
            }}
          >
            x
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px 20px' }}>
          {activeTab === 'cache' && (
            <>
              {/* Bulk Actions */}
              <div style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '16px',
                alignItems: 'center',
              }}>
                <button
                  onClick={handleBulkCache}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '6px',
                    border: '1px solid #4a9eff',
                    background: 'rgba(74, 158, 255, 0.15)',
                    color: '#4a9eff',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Cache Missing (Last {bulkDays} Days)
                </button>
                <select
                  value={bulkDays}
                  onChange={(e) => setBulkDays(parseInt(e.target.value))}
                  style={{
                    padding: '5px 8px',
                    borderRadius: '4px',
                    border: '1px solid #2a3344',
                    background: '#0f1419',
                    color: '#e0e0e0',
                    fontSize: '12px',
                  }}
                >
                  {[3, 5, 7, 14, 30].map(n => (
                    <option key={n} value={n}>{n} days</option>
                  ))}
                </select>
                <button
                  onClick={fetchCacheStatus}
                  disabled={loading}
                  style={{
                    padding: '6px 14px',
                    borderRadius: '6px',
                    border: '1px solid #2a3344',
                    background: '#0f1419',
                    color: '#8a9ab0',
                    fontSize: '12px',
                    cursor: 'pointer',
                  }}
                >
                  {loading ? 'Loading...' : 'Reload'}
                </button>
                {cacheStatus?.startup_status && (
                  <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: 'auto' }}>
                    ES: {cacheStatus.startup_status.es} | Options: {cacheStatus.startup_status.greeks}
                  </span>
                )}
              </div>

              {/* Cache Status Table */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '12px',
                }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #2a3344' }}>
                      {['Date', 'ES', 'Options', 'OI', 'Order Flow', 'Local Files', 'Actions'].map(h => (
                        <th key={h} style={{
                          padding: '8px 10px',
                          textAlign: 'left',
                          color: '#8a9ab0',
                          fontWeight: '600',
                          fontSize: '11px',
                          textTransform: 'uppercase',
                        }} title={h === 'Order Flow' ? 'Imb/Abs/BO/Div' : undefined}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cacheStatus?.dates?.map((d) => (
                      <tr key={d.date} style={{
                        borderBottom: '1px solid rgba(42, 51, 68, 0.5)',
                        background: d.is_today ? 'rgba(74, 158, 255, 0.06)' : undefined,
                      }}>
                        <td style={{ padding: '6px 10px', color: '#e0e0e0', fontFamily: 'monospace' }}>
                          {d.date}
                          {d.is_today && <span style={{ color: '#4a9eff', fontSize: '10px', marginLeft: '6px', fontFamily: 'sans-serif' }}>TODAY</span>}
                        </td>
                        <td style={{ padding: '6px 10px' }}>
                          <span style={{ color: statusColor(d.es.candles, d.es.validated) }}>
                            {d.es.candles}
                          </span>
                          {d.es.validated && <span style={{ color: '#4aff88', marginLeft: '4px', fontSize: '10px' }}>v</span>}
                        </td>
                        <td style={{ padding: '6px 10px' }}>
                          <span style={{ color: statusColor(d.greeks.candles, d.greeks.validated) }}>
                            {d.greeks.candles}
                          </span>
                          {d.greeks.validated && <span style={{ color: '#4aff88', marginLeft: '4px', fontSize: '10px' }}>v</span>}
                        </td>
                        <td style={{ padding: '6px 10px' }}>
                          <span style={{ color: d.oi.cached ? '#4aff88' : '#ff4a4a' }}>
                            {d.oi.cached ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td style={{ padding: '6px 10px' }}>
                          {d.orderflow?.cached ? (
                            <span style={{ color: d.orderflow.validated ? '#4aff88' : '#ffb84a', fontSize: '11px', fontFamily: 'monospace' }}
                              title={`Abs/Aggr (${d.orderflow.trade_count} trades)`}>
                              {d.orderflow.counts?.absorptions || 0}/{d.orderflow.counts?.aggressive_orders || 0}
                            </span>
                          ) : (
                            <span style={{ color: '#ff4a4a' }}>-</span>
                          )}
                          {d.orderflow?.validated && <span style={{ color: '#4aff88', marginLeft: '4px', fontSize: '10px' }}>v</span>}
                        </td>
                        <td style={{ padding: '6px 10px', fontSize: '10px' }}>
                          {['es_trades', 'tcbbo', 'definition', 'statistics'].map(f => (
                            <span key={f} style={{
                              display: 'inline-block',
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              background: d.has_local_files[f] ? '#4aff88' : '#ff4a4a',
                              marginRight: '4px',
                              title: f,
                            }} />
                          ))}
                        </td>
                        <td style={{ padding: '6px 10px' }}>
                          <div style={{ display: 'flex', gap: '4px' }}>
                            {d.is_today ? (
                              <button
                                onClick={handleRefreshToday}
                                disabled={!!cachingDates[d.date]}
                                style={{
                                  padding: '2px 8px',
                                  borderRadius: '3px',
                                  border: '1px solid #4a9eff',
                                  background: 'rgba(74, 158, 255, 0.15)',
                                  color: '#4a9eff',
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  cursor: cachingDates[d.date] ? 'wait' : 'pointer',
                                }}
                              >
                                Refresh
                              </button>
                            ) : (
                              <button
                                onClick={() => handleCacheDate(d.date)}
                                disabled={!!cachingDates[d.date]}
                                style={{
                                  padding: '2px 8px',
                                  borderRadius: '3px',
                                  border: '1px solid #2a3344',
                                  background: cachingDates[d.date] ? '#2a3344' : '#0f1419',
                                  color: cachingDates[d.date] ? '#4a9eff' : '#8a9ab0',
                                  fontSize: '10px',
                                  cursor: cachingDates[d.date] ? 'wait' : 'pointer',
                                }}
                              >
                                {cachingDates[d.date] ? 'Caching...' : 'Cache'}
                              </button>
                            )}
                            <button
                              onClick={() => {
                                handleInvalidate(d.date, 'es');
                                handleInvalidate(d.date, 'greeks');
                                handleInvalidate(d.date, 'oi');
                                handleInvalidate(d.date, 'orderflow');
                              }}
                              style={{
                                padding: '2px 8px',
                                borderRadius: '3px',
                                border: '1px solid #2a3344',
                                background: '#0f1419',
                                color: '#ff9f43',
                                fontSize: '10px',
                                cursor: 'pointer',
                              }}
                            >
                              Invalidate
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {activeTab === 'logs' && (
            <>
              {/* Log Controls */}
              <div style={{
                display: 'flex',
                gap: '8px',
                marginBottom: '12px',
                alignItems: 'center',
              }}>
                <span style={{ fontSize: '11px', color: '#8a9ab0' }}>Level:</span>
                {['ERROR', 'WARNING', 'INFO'].map(lv => (
                  <button
                    key={lv}
                    onClick={() => { setLogLevel(lv); setLogs([]); }}
                    style={{
                      padding: '3px 10px',
                      borderRadius: '4px',
                      border: '1px solid #2a3344',
                      background: logLevel === lv ? '#4a9eff' : 'transparent',
                      color: logLevel === lv ? '#fff' : '#8a9ab0',
                      fontSize: '11px',
                      fontWeight: '600',
                      cursor: 'pointer',
                    }}
                  >
                    {lv}
                  </button>
                ))}
                <span style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: logConnected ? '#4aff88' : '#ff4a4a',
                  marginLeft: '8px',
                }} />
                <span style={{ fontSize: '10px', color: '#6b7280' }}>
                  {logConnected ? 'Connected' : 'Disconnected'}
                </span>
                <button
                  onClick={() => setLogs([])}
                  style={{
                    marginLeft: 'auto',
                    padding: '3px 10px',
                    borderRadius: '4px',
                    border: '1px solid #2a3344',
                    background: '#0f1419',
                    color: '#8a9ab0',
                    fontSize: '11px',
                    cursor: 'pointer',
                  }}
                >
                  Clear
                </button>
              </div>

              {/* Log Container */}
              <div
                ref={logContainerRef}
                onScroll={handleLogScroll}
                style={{
                  background: '#0a0e1a',
                  border: '1px solid #2a3344',
                  borderRadius: '6px',
                  padding: '8px 12px',
                  height: 'calc(80vh - 200px)',
                  overflow: 'auto',
                  fontFamily: 'monospace',
                  fontSize: '11px',
                  lineHeight: '1.5',
                }}
              >
                {logs.length === 0 && (
                  <div style={{ color: '#6b7280', fontStyle: 'italic' }}>
                    Waiting for log entries...
                  </div>
                )}
                {logs.map((entry, i) => (
                  <div key={i} style={{ color: levelColor(entry.level), whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {entry.line}
                  </div>
                ))}
              </div>
            </>
          )}

          {activeTab === 'system' && (
            <>
              {systemError && !systemStatus && (
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '6px',
                  border: '1px solid #ffb84a',
                  background: '#0f1419',
                  color: '#ffb84a',
                  fontSize: '12px',
                  marginBottom: '12px',
                }}>
                  System status endpoint unavailable (pending backend restart).
                </div>
              )}

              {systemStatus && (() => {
                const clients = systemStatus.clients || {};
                const workers = systemStatus.workers || [];
                const schwab = systemStatus.schwab || {};
                const databento = systemStatus.databento || {};
                const streamers = databento.streamers || [];
                const redis = systemStatus.redis || {};
                const perChannel = Object.entries(clients.per_channel || {}).sort((a, b) => b[1] - a[1]);
                const perUser = Object.entries(clients.per_username || {}).sort((a, b) => b[1] - a[1]);
                const subsTotal = perChannel.reduce((acc, [, n]) => acc + n, 0);
                const streamsColor = worstColor([
                  quoteStreamColor(schwab.quote_stream),
                  tokenColor(schwab.token),
                  esTapeColor(databento.es_tape),
                  ...streamers.map(streamerColor),
                ]);

                const card = (label, value, color) => (
                  <div style={{
                    flex: 1,
                    background: '#0f1419',
                    border: '1px solid #2a3344',
                    borderRadius: '6px',
                    padding: '10px 14px',
                  }}>
                    <div style={{ fontSize: '10px', color: '#8a9ab0', textTransform: 'uppercase' }}>{label}</div>
                    <div style={{ fontSize: '20px', fontWeight: 600, color: color || '#4a9eff' }}>{value}</div>
                  </div>
                );

                const sectionTitle = (text) => (
                  <div style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#8a9ab0',
                    textTransform: 'uppercase',
                    margin: '14px 0 6px 0',
                  }}>{text}</div>
                );

                const streamRow = (color, name, details) => (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 10px',
                    borderBottom: '1px solid #2a3344',
                    fontSize: '12px',
                  }}>
                    {dot(color)}
                    <span style={{ color: '#e0e0e0', minWidth: '220px' }}>{name}</span>
                    <span style={{ color: '#8a9ab0', fontFamily: 'monospace', fontSize: '11px' }}>{details}</span>
                  </div>
                );

                const box = { background: '#0f1419', border: '1px solid #2a3344', borderRadius: '6px' };
                const th = { textAlign: 'left', padding: '5px 10px', color: '#8a9ab0', fontSize: '10px', textTransform: 'uppercase', borderBottom: '1px solid #2a3344' };
                const td = { padding: '5px 10px', color: '#c0c8d8', fontSize: '11px', fontFamily: 'monospace', borderBottom: '1px solid #1e2635' };

                const qs = schwab.quote_stream;
                const tok = schwab.token;
                const tape = databento.es_tape;

                return (
                  <div>
                    <div style={{ display: 'flex', gap: '10px', marginBottom: '4px' }}>
                      {card('Clients Connected', clients.total_connections ?? 0)}
                      {card('Subscriptions', subsTotal)}
                      {card('Workers Up', workers.length)}
                      {card('Streams', streamsColor === '#4aff88' ? 'OK' : (streamsColor === '#ffb84a' ? 'CHECK' : 'ISSUE'), streamsColor)}
                    </div>

                    {sectionTitle('Data Streams')}
                    <div style={box}>
                      {streamRow(
                        quoteStreamColor(qs),
                        'Schwab quote stream (SPX/SPY)',
                        qs
                          ? `running ${qs.running ? 'yes' : 'no'}, last tick ${qs.last_tick_age != null ? qs.last_tick_age + 's' : 'never'}, ticks ${(qs.ticks_total ?? 0).toLocaleString()}, ws heartbeats ${qs.heartbeats_total ?? 0}, subs ack ${qs.subs_ack == null ? 'pending' : (qs.subs_ack ? 'yes' : 'NO')}`
                          : 'not running (equity RTH only)'
                      )}
                      {streamRow(
                        tokenColor(tok),
                        'Schwab refresh token',
                        tok && tok.refresh_token_days_left != null
                          ? `${tok.refresh_token_days_left} days left${tok.refresh_token_days_left <= 0 ? ' (EXPIRED)' : ''}`
                          : 'unknown'
                      )}
                      {streamRow(
                        esTapeColor(tape),
                        'Databento ES tape',
                        tape
                          ? `process ${tape.process_alive == null ? 'unknown' : (tape.process_alive ? 'alive' : 'DEAD')}, ${tape.last_trade_age_s != null ? `last trade ${tape.last_trade_age_s}s ago` : 'no trades 60s+'}`
                          : 'no data'
                      )}
                      {streamers.map(st => streamRow(
                        streamerColor(st),
                        `Databento options stream ${st.underlying || '?'}`,
                        `${st.status ? st.status + ', ' : ''}ticks ${(st.ticks_processed ?? 0).toLocaleString()}, last tick ${st.last_tick_age_s != null ? st.last_tick_age_s + 's' : 'never'}, up ${fmtUptime(st.uptime_s)}, heartbeat ${st.heartbeat_age_s != null ? st.heartbeat_age_s + 's' : '-'}`
                      ))}
                      {streamers.length === 0 && streamRow('#ff4a4a', 'Databento options streams', 'no heartbeats (streamers down or not yet connected)')}
                    </div>

                    {sectionTitle('API Workers')}
                    <div style={box}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={th}>PID</th>
                            <th style={th}>Role</th>
                            <th style={th}>Uptime</th>
                            <th style={th}>SSE Conns</th>
                            <th style={th}>Heartbeat</th>
                          </tr>
                        </thead>
                        <tbody>
                          {workers.map(w => (
                            <tr key={w.pid}>
                              <td style={td}>{w.pid}</td>
                              <td style={{ ...td, color: w.role === 'primary' ? '#4a9eff' : '#c0c8d8' }}>{w.role}</td>
                              <td style={td}>{fmtUptime(w.uptime_s)}</td>
                              <td style={td}>{w.sse_connections ?? 0}</td>
                              <td style={td}>{dot(ageColor(w.heartbeat_age_s))}{w.heartbeat_age_s}s</td>
                            </tr>
                          ))}
                          {workers.length === 0 && (
                            <tr><td style={td} colSpan={5}>No worker heartbeats yet.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div style={{ display: 'flex', gap: '10px' }}>
                      <div style={{ flex: 1 }}>
                        {sectionTitle('SSE Channels')}
                        <div style={box}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr><th style={th}>Channel</th><th style={th}>Subscribers</th></tr>
                            </thead>
                            <tbody>
                              {perChannel.map(([ch, n]) => (
                                <tr key={ch}><td style={td}>{ch}</td><td style={td}>{n}</td></tr>
                              ))}
                              {perChannel.length === 0 && (
                                <tr><td style={td} colSpan={2}>No active subscriptions.</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div style={{ flex: 1 }}>
                        {sectionTitle('Connections by User')}
                        <div style={box}>
                          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                              <tr><th style={th}>User</th><th style={th}>Connections</th></tr>
                            </thead>
                            <tbody>
                              {perUser.map(([user, n]) => (
                                <tr key={user}><td style={td}>{user}</td><td style={td}>{n}</td></tr>
                              ))}
                              {perUser.length === 0 && (
                                <tr><td style={td} colSpan={2}>No active connections.</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {sectionTitle('Redis')}
                    <div style={{ ...box, padding: '8px 12px', fontSize: '11px', fontFamily: 'monospace', color: '#c0c8d8', marginBottom: '16px' }}>
                      <div>
                        memory {redis.info?.used_memory_human || '?'}, connected clients {redis.info?.connected_clients ?? '?'}
                      </div>
                      {Object.entries(redis.pool_stats || {}).map(([pool, stats]) => (
                        <div key={pool} style={{ color: '#8a9ab0' }}>
                          {pool}: max {stats.max_connections}{stats.created_connections != null ? `, created ${stats.created_connections}` : ''}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {!systemStatus && !systemError && (
                <div style={{ color: '#6b7280', fontStyle: 'italic', fontSize: '12px' }}>
                  Loading system status...
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
